import {
    Log, SigninRedirectArgs, SigninSilentArgs, SignoutRedirectArgs,
    User, UserManager, UserManagerSettings, WebStorageStateStore
} from 'oidc-client-ts';
import {
    REACT_APP_STS_AUTHORITY, REACT_APP_CLIENT_ID, REACT_APP_RESPONSE_TYPE, REACT_APP_CLIENT_SCOPE
} from '../constants'

class AuthService {
    public userManager: UserManager;
    public isLoggingOut: boolean = false;
    private renewPromise: Promise<User | null> | null = null;
    constructor() {
        const settings: UserManagerSettings = {
            authority: REACT_APP_STS_AUTHORITY,
            client_id: REACT_APP_CLIENT_ID,
            redirect_uri: window.location.origin + '/signin-oidc',
            silent_redirect_uri: window.location.origin + '/silent-renew',
            post_logout_redirect_uri: window.location.origin,
            scope: REACT_APP_CLIENT_SCOPE,
            response_type: REACT_APP_RESPONSE_TYPE,
            response_mode: 'query',
            monitorSession: false,
            automaticSilentRenew: false,
            disablePKCE: false,
            loadUserInfo: true,
            stopCheckSessionOnError: true,
            revokeTokensOnSignout: true,
            userStore: new WebStorageStateStore({ store: window.sessionStorage })
        };

        this.userManager = new UserManager(settings);

        Log.setLogger(console);
        Log.setLevel(Log.WARN);
    }

    public async getUser(): Promise<User | null> {
        return this.userManager.getUser();
    }

    public async login(args?: SigninRedirectArgs): Promise<void> {
        return this.userManager.signinRedirect(args);
    }

    // Single-flight: concurrent callers share one renewal. With refresh token
    // rotation, parallel renews would replay a consumed refresh token and get
    // the whole token family revoked.
    public renewToken(args?: SigninSilentArgs): Promise<User | null> {
        if (!this.renewPromise) {
            this.renewPromise = this.doRenewToken(args)
                .finally(() => { this.renewPromise = null; });
        }
        return this.renewPromise;
    }

    private async doRenewToken(args?: SigninSilentArgs): Promise<User | null> {
        const user = await this.userManager.getUser();
        const orgId = user?.profile?.org_id as string | undefined;
        const extraQueryParams = orgId
            ? { ...args?.extraQueryParams, organization: orgId }
            : args?.extraQueryParams;

        return this.userManager.signinSilent({ ...args, extraQueryParams });
    }

    public async removeUser(): Promise<void> {
        return this.userManager.removeUser();
    }

    public async logout(args?: SignoutRedirectArgs): Promise<void> {
        this.isLoggingOut = true;
        return this.userManager.signoutRedirect(args);
    }

    public async signinRedirectCallback(url?: string): Promise<User> {
        return this.userManager.signinRedirectCallback(url);
    }

    public async signinSilentCallback(url?: string): Promise<void> {
        return this.userManager.signinSilentCallback(url);
    }
}

const authService = new AuthService();
export default authService;
